import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Container, Grid } from '@material-ui/core'
import { ButtonHLPure, HeaderHLPure } from '../../pures'
import {
    ReactFlow,
    Controls,
    Background,
    useNodesState,
    useEdgesState,
    addEdge,
    useReactFlow,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import UserNodeDetails from './conversation-flow/user-node-details'
import BotNodeDetails from './conversation-flow/bot-node-details'
import { useDebounce } from '@uidotdev/usehooks'
import { ConversationNode, EndNode, StartNode } from './conversation-flow/nodes'
import { ContextMenu, EdgeContextMenu } from './conversation-flow/menus'
import StartNodeDetails from './conversation-flow/start-node-details'
import { useTranslation } from 'react-i18next'
import { Label } from '@progress/kendo-react-labels'

const shouldUpdate = (value) => {
    if (!value) return value

    let { nodes, edges } = value
    edges = JSON.stringify(edges)
    nodes = nodes.map(node => ({
        id: node.id,
        data: node.data,
        position: node.position
    }))
    nodes = JSON.stringify(nodes)
    return `${nodes}${edges}`
}

const StepDetails = (props) => {
    const { node } = props
    const { type } = node

    if (type === 'botNode') {
        return <BotNodeDetails {...props} />
    } if (type === 'startNode') {
        return <StartNodeDetails {...props} />
    } else {
        return <UserNodeDetails {...props} />
    }
}

const ChatbotConversationFlowView = (props) => {
    const { isSaving, initialData, saveConversation, error, updatedDate } = props
    const { t } = useTranslation('chatbot', { keyPrefix: 'conversationsConfig.conversations' })
    const { t: tCommon } = useTranslation('common')

    const { updateNodeData } = useReactFlow()
    const nodeTypes = useMemo(() => ({
        startNode: StartNode,
        endNode: EndNode,
        botNode: ConversationNode,
        userNode: ConversationNode
    }), [])

    const [nodes, setNodes, onNodesChange] = useNodesState(initialData?.nodes)
    const [edges, setEdges, onEdgesChange] = useEdgesState(initialData?.edges)
    const [menu, setMenu] = useState(null)
    const [edgeMenu, setEdgeMenu] = useState(null)
    const ref = useRef(null)
    const [selectedNode, setSelectedNode] = useState()

    const debouncedValue = useDebounce(
        useMemo(() => ({ nodes, edges }), [nodes, edges]),
        2000
    )
    const hasMounted = useRef(false)

    useEffect(() => {
        if (hasMounted.current) {
            saveConversation(debouncedValue)
        } else {
            hasMounted.current = true
        }
    }, [shouldUpdate(debouncedValue)])

    const onConnect = useCallback(
        (params) => {
            const sourceNode = nodes.find((node) => node.id === params.source)
            const targetNode = nodes.find((node) => node.id === params.target)

            if (sourceNode.type === 'startNode') {
                // startNode can only connect to botNode
                if (targetNode.type !== 'botNode') return

                // the target userNode cannot be target of another edge
                const isAlreadyTarget = edges.find((edge) => edge.target === targetNode.id)
                if (isAlreadyTarget) return
            }

            // userNode can only connect to botNode
            if (sourceNode.type === 'userNode' && targetNode.type !== 'botNode') return

            if (sourceNode.type === 'botNode') {
                // botNode can only connect to userNode or endNode
                if (targetNode.type !== 'userNode' && targetNode.type !== 'endNode') return

                // the target userNode cannot be target of another edge
                if (targetNode.type === 'userNode') {
                    const isAlreadyTarget = edges.find((edge) => edge.target === targetNode.id)
                    if (isAlreadyTarget) return
                }
            }

            // startNode and userNode can only connect to one target
            if (sourceNode.type === 'startNode' || sourceNode.type === 'userNode') {
                const connectedNodes = edges.filter((edge) => edge.source === sourceNode.id)
                if (connectedNodes.length > 0) return
            }

            // if botNode is connected to an endNode, it cannot connect to anything else
            if (sourceNode.type === 'botNode') {
                const connectedEdges = edges.filter((edge) => edge.source === sourceNode.id)
                if (connectedEdges.length > 0 && targetNode.type === 'endNode') return

                const hasEndNode = connectedEdges.find((edge) => edge.target.includes('end'))
                if (hasEndNode) return
            }

            setEdges((eds) => addEdge(params, eds))
        },
        [nodes, edges, addEdge, setEdges],
    )

    const onEdgeContextMenu = useCallback(
        (event, edge) => {
            event.preventDefault()
            const pane = ref.current.getBoundingClientRect()
            const menuHeight = 40
            const menuWidth = 128

            setEdgeMenu({
                id: edge.id,
                top: event.clientY - pane.top,
                left: event.clientX - pane.left,
                bottom: pane.bottom - event.clientY - menuHeight,
                right: pane.right - event.clientX - menuWidth
            })
        },
        [setEdgeMenu],
    )

    const onNodeContextMenu = useCallback(
        (event, node) => {
            event.preventDefault()  // Prevent native context menu from showing
            const pane = ref.current.getBoundingClientRect()
            const menuHeight = 104
            const menuWidth = 128

            setMenu({
                id: node.id,
                type: node.type,
                top: event.clientY - pane.top,
                left: event.clientX - pane.left,
                bottom: pane.bottom - event.clientY - menuHeight,
                right: pane.right - event.clientX - menuWidth
            })
        },
        [setMenu],
    )

    const onPaneClick = useCallback(() => {
        setMenu(null)
        setEdgeMenu(null)
        setSelectedNode(null)
    }, [setMenu, setSelectedNode])

    const onNodeClick = useCallback((event, node) => {
        if (node.type === 'endNode') return
        setSelectedNode(node)
    }, [setSelectedNode])

    const onSaveNode = (data) => {
        if (!selectedNode) return
        updateNodeData(selectedNode.id, { ...data })
    }

    return (
        <Container className='view-container'>
            <Grid container spacing={2}>
                <Grid item xs={12} >
                    <HeaderHLPure title={t('flow')} />
                </Grid>

                <Grid item xs={12}>
                    <Container maxWidth={'lg'}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} className='button-actions'>
                                <h2 className='conversation-title'>{initialData.name}</h2>
                                {
                                    error && <p className='hint-message-error'>{error}</p>
                                }
                                {
                                    isSaving ?
                                    <Label>{t('saving')}</Label>
                                    : updatedDate && <Label>{t('lastUpdate')}: {updatedDate}</Label>
                                }
                            </Grid>
                            <Grid item xs={12} md={8} style={{ height: '80vh' }}>
                                <ReactFlow
                                    ref={ref}
                                    fitView
                                    nodeTypes={nodeTypes}
                                    nodes={nodes.map((node) => ({
                                        ...node,
                                        data: { ...node.data, openContextMenu: onNodeContextMenu },
                                    }))}
                                    edges={edges}
                                    onNodesChange={onNodesChange}
                                    onEdgesChange={onEdgesChange}
                                    onConnect={onConnect}
                                    onPaneClick={onPaneClick}
                                    onNodeContextMenu={(e) => e.preventDefault()}
                                    onNodeClick={onNodeClick}
                                    onEdgeContextMenu={onEdgeContextMenu}
                                >
                                    <Controls />
                                    <Background variant='dots' gap={12} size={1} />
                                    {menu && <ContextMenu onClick={onPaneClick} t={t} {...menu} />}
                                    {edgeMenu && <EdgeContextMenu onClick={onPaneClick} t={t} {...edgeMenu} />}
                                </ReactFlow>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                {
                                    selectedNode
                                        ? <StepDetails
                                            key={`${selectedNode.type}-${selectedNode.id}`}
                                            node={selectedNode}
                                            onSaveNode={onSaveNode}
                                        />
                                        : <div className='conversation-node-form'>
                                            <p style={{ textAlign: 'center', color: 'var(--neutral-400)', fontStyle: 'italic' }}>{t('selectANode')}</p>
                                        </div>
                                }
                            </Grid>
                        </Grid>
                    </Container>
                </Grid>
            </Grid>
        </Container>
    )
}

export default ChatbotConversationFlowView